import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useApi } from '../hooks/useApi';
import { updatePersona } from '../services/apiService';
import { useUserStatus } from '../context/UserStatusContext';
import { IPIP_NEO_FACETS } from '../content/personaContent';
import ImageUpload from '../components/initializer/ImageUpload';
import PersonaImage from '../components/molecules/PersonaImage';
import styles from './EditPersonaPage.module.css';

const calculateDomainScore = (domainFacets = {}) => {
  const scores = Object.values(domainFacets);
  if (scores.length === 0) return 50;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};

function EditPersonaPage() {
  const { personaId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { refreshUserStatus, bustImageCache } = useUserStatus();

  const {
    data: initialPersona,
    loading,
    error,
  } = useApi(`/v1/personas/${personaId}`);

  const [formData, setFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);

  const formatLabel = (key) => {
    const words = key.replace(/_/g, ' ').split(' ');
    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (initialPersona) {
      setFormData(initialPersona);
    }
  }, [initialPersona]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSimpleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNestedChange = (category, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const handleSliderChange = (domain, facet, value) => {
    setFormData((prev) => ({
      ...prev,
      personalityData: {
        ...prev.personalityData,
        [domain]: {
          ...prev.personalityData[domain],
          [facet]: parseInt(value, 10),
        },
      },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError(null);
    try {
      const accessToken = await getAccessTokenSilently();

      let payload = { ...formData };
      if (payload.personalityData) {
        payload.personalityData = {
          ...payload.personalityData,
          opennessScore: calculateDomainScore(payload.personalityData.openness),
          conscientiousnessScore: calculateDomainScore(
            payload.personalityData.conscientiousness
          ),
          extraversionScore: calculateDomainScore(
            payload.personalityData.extraversion
          ),
          agreeablenessScore: calculateDomainScore(
            payload.personalityData.agreeableness
          ),
          neuroticismScore: calculateDomainScore(
            payload.personalityData.neuroticism
          ),
        };
      }

      // Send the complete payload with the scores to the API
      await updatePersona(personaId, payload, accessToken);

      bustImageCache(personaId);
      await refreshUserStatus();

      // Navigate to the correct page based on type
      if (formData.type === 'digital-me') {
        navigate('/passport');
      } else {
        navigate('/profiles');
      }
    } catch (err) {
      setSaveError(err.message);
      setIsSaving(false);
    }
  };

  if (loading) return <p>Loading persona...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!formData) return <p>Persona data could not be loaded.</p>;

  return (
    <div className={styles.editPageContainer}>
      <div className="page-header">
        <h2>Edit: {formData.personaName}</h2>
        <div>
          <button
            onClick={() => navigate('/profiles')}
            className="action-button secondary"
            style={{ marginRight: '1rem' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="action-button primary"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {saveError && (
        <p className="errorText" style={{ textAlign: 'center' }}>
          {saveError}
        </p>
      )}

      <form onSubmit={handleSave} className={styles.editForm}>
        {/* --- Core Identity Section --- */}
        <fieldset>
          <legend>Core Identity</legend>
          <div className={styles.formGroup}>
            <label>Avatar</label>
            {newImagePreview ? (
              <img
                src={newImagePreview}
                alt="New avatar preview"
                className={styles.avatarImage}
              />
            ) : (
              <PersonaImage
                personaId={personaId}
                alt={formData.personaName}
                className={styles.avatarImage}
                isOwner={true}
                cacheKey={formData.updatedAt}
              />
            )}
            <div className={styles.newAvatar}>
              <label>Upload New</label>
              <ImageUpload
                onUploadSuccess={({ finalImageUrl, previewUrl }) => {
                  setFormData((prev) => ({ ...prev, imageUrl: finalImageUrl }));
                  setNewImagePreview(previewUrl);
                }}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="personaName">Name</label>
            <input
              type="text"
              id="personaName"
              name="personaName"
              value={formData.personaName}
              onChange={handleSimpleChange}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="summary">Summary</label>
            <textarea
              id="summary"
              name="summary"
              rows="3"
              value={formData.summary}
              onChange={handleSimpleChange}
            />
          </div>
        </fieldset>

        {formData.type !== 'digital-me' && (
          <fieldset>
            <legend>Sharing & Visibility</legend>
            <div className={styles.publicToggle}>
              <input
                type="checkbox"
                id="isPublic"
                name="isPublic"
                checked={formData.isPublic || false}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="isPublic">
                Make this persona available to the public in the Community
                Library.
              </label>
            </div>
          </fieldset>
        )}

        {/* --- Narrative Data Section --- */}
        <fieldset>
          <legend>Narrative & Backstory</legend>
          {formData.narrativeData &&
            Object.keys(formData.narrativeData).map((key) => (
              <div className={styles.formGroup} key={key}>
                <label htmlFor={key}>{formatLabel(key)}</label>
                <textarea
                  id={key}
                  name={key}
                  rows="3"
                  value={formData.narrativeData[key]}
                  onChange={(e) =>
                    handleNestedChange('narrativeData', key, e.target.value)
                  }
                />
              </div>
            ))}
        </fieldset>

        {/* --- Personality Sliders Section --- */}
        {Object.entries(IPIP_NEO_FACETS).map(([domainKey, domainData]) => (
          <fieldset key={domainKey}>
            <legend>{domainData.title}</legend>
            {Object.entries(domainData.facets).map(([facetKey, facetData]) => (
              <div className={styles.sliderGroup} key={facetKey}>
                <label htmlFor={facetKey}>{facetData.name}</label>
                <input
                  type="range"
                  id={facetKey}
                  name={facetKey}
                  min="1"
                  max="100"
                  value={formData.personalityData[domainKey]?.[facetKey] || 50}
                  onChange={(e) =>
                    handleSliderChange(domainKey, facetKey, e.target.value)
                  }
                />
                <span className={styles.sliderValue}>
                  {formData.personalityData[domainKey]?.[facetKey] || 50}
                </span>
              </div>
            ))}
          </fieldset>
        ))}
      </form>
    </div>
  );
}

export default withAuthenticationRequired(EditPersonaPage);
